import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, ResourceRef, signal, TemplateRef, viewChild } from '@angular/core'
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms'
import {
  CodeByTypeApiResponse,
  HullSparesCoverProvisionsForm
} from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import { AmountWithCurrencyForm, FreetextSection } from '@features/certificate/models/certificate-shared.model'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { CertificateFormService } from '@features/certificate/services/certificate-form/certificate-form.service'

import { AlbertInputWrapper } from '@features/certificate/components/certificate-input-wrapper/albert-input-wrapper'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { AlbertLimitComponent } from '@shared/components/albert-limit/albert-limit.component'
import { ButtonDirective } from 'primeng/button'
import { Tooltip } from 'primeng/tooltip'
import { CertificateFreetextSectionComponent } from '@features/certificate/components/certificate-freetext-section/certificate-freetext-section.component'
import {
  ENGINE_GROSSUP_CLAUSE,
  ENGINE_GROSSUP_CLAUSE_OPTIONS,
  FLEET_LIST_FORMAT,
  FLEET_LIST_FORMAT_OPTIONS,
  OBJECT_TYPE,
  OBJECT_TYPE_OPTIONS,
  ObjectType
} from '@features/certificate/models/certificate-select-options.model'
import {
  additionalInsuredSection,
  breachOfWarrantySection,
  freetextSection,
  noLiabilityForPremiumsSection,
  noticeOfCancellationNoWarSection,
  setupFreetextSections
} from '@features/certificate/utils/freetext-utils'
import { TitledEngineForm } from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import { AlbertTextareaComponent } from '@shared/components/albert-textarea/albert-textarea.component'
import { AlbertMultiselectComponent } from '@shared/components/albert-multiselect/albert-multiselect.component'
import { HttpClient } from '@angular/common/http'
import { hasActualValue, onValueChanges, syncFormCategoryState, valueWithChanges } from '@core/utils/form-utils'
import { environment } from '@environments/environment'
import { AircraftScheduleApiResponse, EquipmentScheduleApiResponse, PolicyAssetsApiResponse } from '@features/insured/models/policy/policy.model'
import { filter, map, merge, of, startWith, switchMap } from 'rxjs'
import { PolicyService } from '@features/insured/services/policy/policy.service'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { HULL_SPARES_COVER_PROVISIONS_VISIBILITY } from '@features/certificate/components/hull-spares-cover-provisions/hull-spares-cover-provisions.visibility'
import { IfCategoryDirective } from '@features/certificate/directives/if-category.directive'
import { aircraftScheduleUrl, equipmentScheduleUrl } from '@core/utils/origami-utils'
import { AlbertModalService } from '@services/albert-modal.service'
import { FormatCurrencyAmountPipe } from '@shared/pipes/format-currency-amount.pipe'
import { TableModule } from 'primeng/table'
import { Tag } from 'primeng/tag'
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop'

const LEASE_VALUE_FORMATTER = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

type FreetextSectionKey =
  | 'hullInsurance'
  | 'hullWarInsurance'
  | 'sparesAllRiskInsurance'
  | 'waiverOfSubrogation'
  | 'noLiabilityForPremiums'
  | 'noticeOfCancellationNoWar'
  | 'breachOfWarranty'
  | 'additionalInsured'

@Component({
  selector: 'albert-hull-spares-cover-provisions',
  imports: [
    ReactiveFormsModule,
    AlbertInputWrapper,
    AlbertSelectComponent,
    AlbertCheckboxComponent,
    AlbertInputComponent,
    AlbertLimitComponent,
    ButtonDirective,
    Tooltip,
    CertificateFreetextSectionComponent,
    AlbertTextareaComponent,
    AlbertMultiselectComponent,
    IfCategoryDirective,
    TableModule,
    Tag,
    FormatCurrencyAmountPipe
  ],
  templateUrl: './hull-spares-cover-provisions.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HullSparesCoverProvisionsComponent extends CertificateBaseComponent implements OnInit {
  public readonly form = input.required<FormGroup<HullSparesCoverProvisionsForm>>()
  public readonly policyId = input<string | null | undefined>()
  public readonly fleetListAsAttachmentLocked = input<boolean>(false)
  public readonly objectTypeOptions = OBJECT_TYPE_OPTIONS
  public readonly objectType = OBJECT_TYPE
  public readonly engineGrossupClauseOptions = ENGINE_GROSSUP_CLAUSE_OPTIONS
  public readonly fleetListFormatOptions = FLEET_LIST_FORMAT_OPTIONS
  public readonly visibility = HULL_SPARES_COVER_PROVISIONS_VISIBILITY

  public readonly CertificateCategory = CertificateCategory
  public readonly freetextSections: FreetextSection<FreetextSectionKey>[] = [
    freetextSection('hullInsurance', 'Hull Insurance Freetext', 'hull-insurance', 'Hull Insurance (General Confirmation)'),
    freetextSection('hullWarInsurance', 'Hull War Insurance Freetext', 'hull-war-insurance', 'Hull War Insurance (General Confirmation)'),
    freetextSection(
      'sparesAllRiskInsurance',
      'Spares All Risk Insurance Freetext',
      'spares-all-risk-insurance',
      'Spares All Risk Insurance (General Confirmation)'
    ),
    freetextSection('waiverOfSubrogation', 'Waiver of Subrogation Freetext', 'waiver-of-subrogation', 'Waiver of Subrogation'),
    noLiabilityForPremiumsSection('No Liability for Premiums'),
    noticeOfCancellationNoWarSection('notice-of-cancellation'),
    breachOfWarrantySection(),
    additionalInsuredSection('hull', 'Freetext Hull Provisions'),
  ]
  public readonly freetextSectionsByKey: Record<FreetextSectionKey, FreetextSection> =
    setupFreetextSections(this.form, this.freetextSections)
  public readonly fleetListPreviewTemplate = viewChild.required<TemplateRef<unknown>>('fleetListPreview')
  public readonly matchedAircraftSchedule = signal<AircraftScheduleApiResponse | undefined>(undefined)
  public readonly hullAllRiskDeductibleAmountLocked = signal(false)
  public readonly matchedEquipmentSchedule = signal<EquipmentScheduleApiResponse | undefined>(undefined)
  public readonly fleetListPreviewRows = signal<AircraftScheduleApiResponse[]>([])
  private readonly certificateFormService = inject(CertificateFormService)
  private readonly http = inject(HttpClient)
  private readonly policyService = inject(PolicyService)
  private readonly modalService = inject(AlbertModalService)
  private readonly fleetList = rxResource<PolicyAssetsApiResponse, string | null | undefined>({
    params: () => this.policyId(),

    stream: ({ params: id }) => {
      if (hasActualValue(id)) {
        return this.policyService.getAssets(id)
      }
      return of({ aircraftSchedules: [], equipmentSchedules: [] })
    }
  })
  public readonly aircraftSchedules = computed(() => {
    const assets = this.fleetList.value()
    return assets?.aircraftSchedules
  })
  public readonly equipmentSchedules = computed(() => {
    const assets = this.fleetList.value()
    return assets?.equipmentSchedules
  })
  private readonly aircraftResource = this.createCodeTypeResource(OBJECT_TYPE.AIRCRAFT)
  public readonly aircraftTypeOptions = computed(() => (this.aircraftResource.value() ?? []).map(o => o.description))
  private readonly selectedAircraftTypeFilter = toSignal(
    toObservable(this.form).pipe(
      switchMap(form => valueWithChanges(form.controls.aircraftTypeFilter)),
      map(value => value ?? [])
    ),
    { initialValue: [] }
  )
  public readonly aircraftTypeFilterOptions = computed(() => {
    const fleetTypes = (this.aircraftSchedules() ?? [])
      .map(schedule => schedule.aircraftType)
      .filter(hasActualValue)
    const selectedTypes = this.selectedAircraftTypeFilter()
    return [...new Set([...fleetTypes, ...selectedTypes])].sort((a, b) => a.localeCompare(b))
  })
  private readonly equipmentResource = this.createCodeTypeResource(OBJECT_TYPE.ENGINE)
  public readonly equipmentTypeOptions = computed(() => this.equipmentResource.value() ?? [])
  private readonly engineResource = this.createCodeTypeResource(OBJECT_TYPE.ENGINE)
  public readonly engineTypeOptions = computed(() => this.engineResource.value() ?? [])

  constructor() {
    super()

    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      syncFormCategoryState(this.form(), category, this.visibility)
    })

    // The serial numbers are only required for some object types and categories.
    toObservable(computed(() => ({ form: this.form(), category: this.categoryService.activeCertificateCategory() }))).pipe(
      switchMap(({ form }) => onValueChanges(form, form.controls.objectType)),
      takeUntilDestroyed()
    ).subscribe(form => this.revalidateObjectTypeGatedControls(form))

    toObservable(this.form).pipe(
      switchMap(form => onValueChanges(form, form.controls.leaseValues)),
      takeUntilDestroyed()
    ).subscribe(form => this.updateLeaseValuesString(form))

    toObservable(computed(() => ({
      form: this.form(),
      category: this.categoryService.activeCertificateCategory(),
      schedules: this.aircraftSchedules() ?? []
    }))).pipe(
      switchMap(state => onValueChanges(state, state.form.controls.aircraftSerialNumber, state.form.controls.aircraftRegistration)),
      takeUntilDestroyed()
    ).subscribe(state => this.updateAircraftTypeFromSchedule(state.form, state.category, state.schedules))

    toObservable(computed(() => ({
      form: this.form(),
      category: this.categoryService.activeCertificateCategory(),
      matchedAircraftSchedule: this.matchedAircraftSchedule(),
      canEdit: this.canEdit()
    }))).pipe(
      switchMap(state => merge(
        of(false),
        state.form.controls.objectType.valueChanges.pipe(map(() => false)),
        state.form.controls.hullAllRiskDeductible.valueChanges.pipe(map(value => value === true))
      ).pipe(map(ticked => ({ ...state, ticked })))),
      takeUntilDestroyed()
    ).subscribe(({ form, category, matchedAircraftSchedule, canEdit, ticked }) => {
      this.updateHullAllRiskDeductibleAmount(form, category, matchedAircraftSchedule, canEdit)
      if (ticked) {
        form.controls.hullAllRiskDeductibleAmount.markAllAsTouched()
      }
    })

    toObservable(computed(() => ({
      form: this.form(),
      category: this.categoryService.activeCertificateCategory(),
      schedules: this.equipmentSchedules() ?? []
    }))).pipe(
      switchMap(state => onValueChanges(state, state.form.controls.equipmentSerialNumber, state.form.controls.objectType)),
      takeUntilDestroyed()
    ).subscribe(state => this.updateEquipmentTypeFromSchedule(state.form, state.category, state.schedules))

    toObservable(this.form).pipe(
      switchMap(form => {
        const checkboxCtrl = form.controls.engineGrossUpClauseFreetextCheckbox
        return checkboxCtrl.valueChanges.pipe(
          map(() => checkboxCtrl.dirty),
          startWith(false),
          map(userToggled => ({ form, userToggled }))
        )
      }),
      takeUntilDestroyed()
    ).subscribe(({ form, userToggled }) => this.resetEngineGrossupClause(form, userToggled))

    toObservable(this.form).pipe(
      switchMap(form => form.controls.addFleetListAsAttachment.valueChanges.pipe(
        filter(checked => checked !== true),
        map(() => form)
      )),
      takeUntilDestroyed()
    ).subscribe(form => {
      form.controls.columnConfiguration.setValue(FLEET_LIST_FORMAT.DEFAULT)
      form.controls.filterByAircraftType.setValue(false)
      form.controls.aircraftTypeFilter.setValue([])
      form.controls.includePlannedAircraft.setValue(false)
    })

    toObservable(this.form).pipe(
      switchMap(form => form.controls.filterByAircraftType.valueChanges.pipe(
        filter(checked => checked !== true),
        map(() => form)
      )),
      takeUntilDestroyed()
    ).subscribe(form => form.controls.aircraftTypeFilter.setValue([]))
  }

  public get leaseValues(): FormArray<FormGroup<AmountWithCurrencyForm>> {
    return this.form().controls.leaseValues
  }

  public get titledEngines(): FormArray<FormGroup<TitledEngineForm>> {
    return this.form().controls.titledEngines
  }

  public ngOnInit(): void {
    // Seeding the first empty row is not a user edit, so unlike `onAddLeaseValue` it must not mark
    // the form dirty: that would stop the autosave re-baselining on hydration and trigger a PUT.
    if (this.leaseValues.length === 0) {
      this.leaseValues.push(this.certificateFormService.buildAmountWithCurrencyGroup())
    }
  }

  public onAddLeaseValue(): void {
    if (this.leaseValues.length < 10) {
      this.leaseValues.markAsDirty()
      this.leaseValues.push(this.certificateFormService.buildAmountWithCurrencyGroup())
    }
  }

  public onRemoveLeaseValue(index: number): void {
    if (this.leaseValues.length > 0) {
      this.leaseValues.markAsDirty()
      this.leaseValues.removeAt(index)
    }
  }

  public onAddTitledEngine(): void {
    if (this.titledEngines.length < 4) {
      this.titledEngines.markAsDirty()
      const group = this.certificateFormService.buildTitledEngineGroup()
      this.titledEngines.push(group)
      // The serial number's required gate reads `objectType` from the form root, so it can only be
      // resolved once the row is attached.
      group.controls.serialNumber.updateValueAndValidity()
    }
  }

  public onRemoveTitledEngines(index: number): void {
    if (this.titledEngines.length > 0) {
      this.titledEngines.markAsDirty()
      this.titledEngines.removeAt(index)
    }
  }

  public onPreviewFleetList(): void {
    this.fleetListPreviewRows.set(this.buildFleetListPreviewRows())
    this.modalService.show({
      title: 'Preview Fleet List',
      type: 'info',
      closable: true,
      styleClass: 'fleet-list-preview-dialog',
      contentTemplate: this.fleetListPreviewTemplate(),
      actions: [{
        label: 'Close',
        position: 'right',
        severity: 'secondary',
        callback: () => this.modalService.hide()
      }]
    })
  }

  public getStatusSeverity(status: string): 'success' | 'warn' | 'secondary' | 'contrast' | 'danger' {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'PLANNED':
        return 'warn'
      case 'TBA':
        return 'secondary'
      default:
        return 'danger'
    }
  }

  public onOpenAircraftScheduleInOrigami(externalId: string): void {
    window.open(aircraftScheduleUrl(externalId), '_blank')
  }

  public onOpenEquipmentScheduleInOrigami(externalId: string): void {
    window.open(equipmentScheduleUrl(externalId), '_blank')
  }

  private revalidateObjectTypeGatedControls(form: FormGroup<HullSparesCoverProvisionsForm>): void {
    form.controls.aircraftSerialNumber.updateValueAndValidity({ emitEvent: false })
    form.controls.aircraftRegistration.updateValueAndValidity({ emitEvent: false })
    form.controls.equipmentSerialNumber.updateValueAndValidity({ emitEvent: false })
    form.controls.titledEngines.controls.forEach(
      group => group.controls.serialNumber.updateValueAndValidity({ emitEvent: false })
    )
  }

  private updateLeaseValuesString(form: FormGroup<HullSparesCoverProvisionsForm>): void {
    const parts = form.controls.leaseValues.controls
      .map(g => {
        const amount = g.controls.amount.value
        const currency = g.controls.currency.value
        if (amount !== null && currency !== null) {
          return `${LEASE_VALUE_FORMATTER.format(amount)} (${currency})`
        }
        return null
      })
      .filter(Boolean)
    form.controls.leaseValuesString.setValue(parts.join(' plus '), { emitEvent: false })
  }

  private updateAircraftTypeFromSchedule(
    form: FormGroup<HullSparesCoverProvisionsForm>,
    category: CertificateCategory | null,
    schedules: AircraftScheduleApiResponse[]
  ): void {
    const serialNumber = form.controls.aircraftSerialNumber.value
    const registration = form.controls.aircraftRegistration.value
    const matchingSchedule = schedules.find(
      schedule => schedule.serialNumber === serialNumber && schedule.registration === registration
    )
    this.matchedAircraftSchedule.set(matchingSchedule)

    if (matchingSchedule && !hasActualValue(form.controls.aircraftTypeForCertificates.value)) {
      form.controls.aircraftTypeForCertificates.setValue(matchingSchedule.aircraftType, { emitEvent: false })
    }

    if (category !== CertificateCategory.LEASE) {
      return
    }
    if (matchingSchedule) {
      form.controls.aircraftType.setValue([matchingSchedule.aircraftType], { emitEvent: false })
    } else {
      form.controls.aircraftType.setValue(null, { emitEvent: false })
    }
  }

  private updateHullAllRiskDeductibleAmount(
    form: FormGroup<HullSparesCoverProvisionsForm>,
    category: CertificateCategory | null,
    matchedAircraftSchedule: AircraftScheduleApiResponse | undefined,
    canEdit: boolean
  ): void {
    const isCheckboxGatedCategory = category === CertificateCategory.AUTHORITIES || category === CertificateCategory.CONTRACTUAL
    const isFleetDrivenAircraft = category === CertificateCategory.LEASE
      && form.controls.objectType.value === OBJECT_TYPE.AIRCRAFT
      && matchedAircraftSchedule !== undefined
    this.hullAllRiskDeductibleAmountLocked.set(isFleetDrivenAircraft)
    const shouldBeEnabled = canEdit
      && (!isCheckboxGatedCategory || form.controls.hullAllRiskDeductible.value === true)
      && !isFleetDrivenAircraft
    if (shouldBeEnabled) {
      form.controls.hullAllRiskDeductibleAmount.enable()
    } else {
      form.controls.hullAllRiskDeductibleAmount.disable()
    }
  }

  private updateEquipmentTypeFromSchedule(
    form: FormGroup<HullSparesCoverProvisionsForm>,
    category: CertificateCategory | null,
    schedules: EquipmentScheduleApiResponse[]
  ): void {
    const serialNumber = form.controls.equipmentSerialNumber.value
    const matchingSchedule = schedules.find(schedule => schedule.serialNumber === serialNumber)
    this.matchedEquipmentSchedule.set(matchingSchedule)

    if (matchingSchedule && !hasActualValue(form.controls.equipmentTypeForCertificate.value)) {
      form.controls.equipmentTypeForCertificate.setValue(matchingSchedule.equipmentType, { emitEvent: false })
    }

    if (category !== CertificateCategory.LEASE) {
      return
    }
    const objectTypeValue = form.controls.objectType.value
    if (objectTypeValue !== OBJECT_TYPE.ENGINE && objectTypeValue !== OBJECT_TYPE.EQUIPMENT) {
      return
    }
    if (matchingSchedule) {
      form.controls.equipmentType.setValue(matchingSchedule.equipmentType, { emitEvent: false })
    } else {
      form.controls.equipmentType.setValue(null, { emitEvent: false })
    }
  }

  private resetEngineGrossupClause(form: FormGroup<HullSparesCoverProvisionsForm>, userToggled: boolean): void {
    const engineGrossupClauseCtrl = form.controls.engineGrossupClause
    if (form.controls.engineGrossUpClauseFreetextCheckbox.value !== true) return
    if (engineGrossupClauseCtrl.value === ENGINE_GROSSUP_CLAUSE.NONE) return
    if (userToggled) {
      engineGrossupClauseCtrl.markAsDirty()
    }
    engineGrossupClauseCtrl.setValue(ENGINE_GROSSUP_CLAUSE.NONE, { emitEvent: userToggled })
  }

  private buildFleetListPreviewRows(): AircraftScheduleApiResponse[] {
    const form = this.form()
    const includePlanned = form.controls.includePlannedAircraft.value === true
    const filterByType = form.controls.filterByAircraftType.value === true
    const selectedTypes = form.controls.aircraftTypeFilter.value ?? []

    return (this.aircraftSchedules() ?? [])
      .filter(schedule => schedule.objectStatus === 'ACTIVE' || (includePlanned && schedule.objectStatus === 'PLANNED'))
      .filter(schedule => !filterByType || selectedTypes.length === 0 || selectedTypes.includes(schedule.aircraftType))
  }

  private createCodeTypeResource(type: ObjectType): ResourceRef<CodeByTypeApiResponse[] | undefined> {
    return rxResource<CodeByTypeApiResponse[], ObjectType>({
      params: () => type,
      stream: ({ params: type }) =>
        this.http.get<CodeByTypeApiResponse[]>(
          `${environment.referenceApiUrl}/codeByType`,
          { params: { type: type.toUpperCase() } }
        )
    })
  }
}
